/**
 * "X ITEMS NEED YOU" — WHICH, WHY, and WHAT TO DO. Chip, field test, 2026-08-18.
 *
 *   *"An error at the top of the app said 'X items need you', but it gave no
 *    clarification about what those items were, how to fix the issue, or what
 *    to do. That flag at the top is causing confusion."*
 *
 * ── What was true ───────────────────────────────────────────────────────────
 * The sync pill counted the parked outbox rows and said "5 photos need you". It
 * was a `<span>`: tapping it did nothing, and the one sentence it had — which
 * ALREADY carried the first parked row's plain reason — sat in a `title`
 * attribute, which a phone never shows. No screen anywhere enumerated the rows.
 * A parked photo's reason existed on exactly one surface: the tile under the
 * picture, inside the section it was taken in, behind a collapsed "Note &
 * photos" drawer if it was a question photo. A parked DOCUMENT or SUBMISSION
 * had no surface at all — nothing on the phone could retry it.
 *
 * So the app knew (D-021), and would not say. The engine had every fact:
 * `SyncAttention` names the row, its kind, its survey, its photo, which side
 * failed, whether a retry can help, and a plain sentence saying why. What was
 * missing was one place that puts those facts next to the words a surveyor
 * recognises — the survey's name, the section's room-qualified title, the shot
 * or the question the photo was OF — and next to the one action that can work.
 *
 * ── What this module is ─────────────────────────────────────────────────────
 * The pure model of that place. `attentionRows` joins the engine's attention
 * list with the documents on this device and produces, per survey, one row per
 * parked item. It composes NOTHING the engine did not say: `reason` is
 * `SyncAttention.reason` verbatim, `canRetry` is the engine's, and the route is
 * `routeForSection` — the same mapping the last look and the close screen's gap
 * list use, so no two lists send a surveyor to different screens for one
 * section. What it adds is names, and names only.
 *
 * Pure: no React, no store, no clock. `tests/unit/attention.test.ts`.
 */

import { byId } from '../../core/catalog';
import { buildSurveySections, qualifyTitle } from '../../core/sections';
import type { PhotoRef, SectionInstance, SurveyDocument } from '../../core/types';
import type { OutboxKind, StoredSurvey } from '../../db/store';
import type { SyncAttention } from '../../db/sync';
import type { Route } from '../routing';
import { routeForSection } from './reckoning';
import { roomOf } from './results';
import { summarize } from './surveyList';

/* ─────────────────────────────── the shapes ─────────────────────────────── */

export interface AttentionRow {
  /** The outbox row. Pass to `SyncEngine.retryBlocked` to try it once more. */
  itemId: string;
  kind: OutboxKind;
  surveyId: string;
  photoId?: string;
  /** Room-qualified — "Machine Room 1 · Electrical" — so the row reads alone. */
  sectionTitle: string;
  /**
   * What the row IS, in the surveyor's words: the shot's label, the question's
   * label, "A photo of <section>", or — for a non-photo row — "The survey
   * itself" / "Sending the survey".
   */
  what: string;
  /** The engine's own sentence. Never composed here. */
  reason: string;
  /** The server's words, for the office. */
  detail: string;
  /** Whether "Try sending it again" can possibly help — the engine's answer. */
  canRetry: boolean;
  /**
   * Where to go about it. The screen that holds the photo (its section), or the
   * building / submit screen for a document / submission row. `null` when this
   * device no longer holds the survey — there is nowhere to send anyone.
   */
  route: Route | null;
}

export interface AttentionGroup {
  surveyId: string;
  surveyTitle: string;
  rows: AttentionRow[];
}

/* ─────────────────────────────── the model ──────────────────────────────── */

const ORPHAN_TITLE = 'A survey no longer on this phone';

/**
 * Every parked row, grouped by survey in the DEVICE's survey order.
 *
 * Grouped by the device's order and not the outbox's, deliberately: a list that
 * reorders itself when one row is retried is a list nobody can read. Rows for a
 * survey this device no longer holds are kept — the row is real and its reason
 * is real — under one honest heading, with nowhere to go.
 */
export function attentionRows(
  attention: readonly SyncAttention[],
  records: readonly StoredSurvey[],
): AttentionGroup[] {
  const bySurvey = new Map<string, StoredSurvey>();
  for (const record of records) bySurvey.set(record.surveyId, record);

  const groups = new Map<string, AttentionGroup>();
  const order: string[] = [];
  const claim = (surveyId: string): AttentionGroup => {
    let group = groups.get(surveyId);
    if (!group) {
      const record = bySurvey.get(surveyId);
      group = {
        surveyId,
        surveyTitle: record ? summarize(record).title : ORPHAN_TITLE,
        rows: [],
      };
      groups.set(surveyId, group);
      order.push(surveyId);
    }
    return group;
  };

  // The device's order first, so surveys list the way the home screen does…
  for (const record of records) {
    if (attention.some((entry) => entry.surveyId === record.surveyId)) claim(record.surveyId);
  }
  // …then anything parked for a survey the device no longer holds.
  for (const entry of attention) {
    const record = bySurvey.get(entry.surveyId);
    claim(entry.surveyId).rows.push(rowFor(entry, record?.document));
  }

  return order.map((surveyId) => groups.get(surveyId)!);
}

function rowFor(entry: SyncAttention, doc: SurveyDocument | undefined): AttentionRow {
  const base = {
    itemId: entry.itemId,
    kind: entry.kind,
    surveyId: entry.surveyId,
    ...(entry.photoId ? { photoId: entry.photoId } : {}),
    reason: entry.reason,
    detail: entry.detail,
    canRetry: entry.canRetry !== false,
  };

  if (!doc) {
    return { ...base, sectionTitle: ORPHAN_TITLE, what: whatFor(entry.kind), route: null };
  }

  if (entry.kind !== 'photo') {
    return {
      ...base,
      sectionTitle: 'This survey',
      what: whatFor(entry.kind),
      route: nonPhotoRoute(entry.kind, doc.surveyId),
    };
  }

  const photo = doc.photos.find((candidate) => candidate.id === entry.photoId);
  const sections = buildSurveySections(doc);
  const section = photo
    ? sections.find((candidate) => candidate.id === photo.sectionInstanceId)
    : undefined;
  const sectionTitle = section ? titleOf(doc, section) : 'This survey';

  return {
    ...base,
    sectionTitle,
    what: photo && section ? photoLabel(photo, sections, sectionTitle) : 'A photo',
    route: routeForSection(doc.surveyId, section),
  };
}

/**
 * What a photo is OF: the shot it satisfies, the question it hangs on, or its
 * section. The shot is looked up across EVERY section's list, by its current id
 * or by the address it used to have (`RecommendedShot.formerly`) — so a panel
 * photo taken on the building screen before Amendment 6 still reads as the
 * panel, not as "a photo of Building".
 */
function photoLabel(
  photo: PhotoRef,
  sections: readonly SectionInstance[],
  sectionTitle: string,
): string {
  if (photo.shotListId) {
    for (const candidate of sections) {
      const shot = candidate.shotList.find(
        (entry) =>
          (entry.id === photo.shotListId && candidate.id === photo.sectionInstanceId) ||
          (entry.formerly !== undefined &&
            entry.formerly.id === photo.shotListId &&
            entry.formerly.sectionInstanceId === photo.sectionInstanceId),
      );
      if (shot) return shot.label;
    }
  }
  if (photo.questionId) {
    const question = byId(photo.questionId);
    if (question) return question.label;
  }
  return `A photo of ${sectionTitle}`;
}

function titleOf(doc: SurveyDocument, section: SectionInstance): string {
  const room = roomOf(doc, section.scope);
  return room ? qualifyTitle(room.name, section.title) : section.title;
}

/** The non-photo rows, in the words a surveyor would use for them. */
function whatFor(kind: OutboxKind): string {
  switch (kind) {
    case 'photo':
      return 'A photo';
    case 'photo_thumb':
      return 'The small preview of a photo';
    case 'document':
      return 'The survey itself';
    case 'submission':
      return 'Sending the survey';
    case 'seal':
      return 'Telling the office the survey is finished';
    case 'create':
      return 'Telling the office this survey exists';
    case 'discard':
      return 'Removing this survey';
  }
}

function nonPhotoRoute(kind: OutboxKind, surveyId: string): Route {
  return kind === 'submission' ? { name: 'submit', surveyId } : { name: 'building', surveyId };
}

/* ─────────────────────────── the sentence on top ────────────────────────── */

/**
 * The calm lead. Reassurance first, then the ask (D-015) — and never the words
 * "error", "failed" or "invalid": these rows are decisions waiting, not faults.
 */
export function attentionSummary(groups: readonly AttentionGroup[]): string {
  const rows = groups.flatMap((group) => group.rows);
  if (rows.length === 0) {
    return 'Nothing needs you. Everything on this phone has reached the office, or is on its way.';
  }
  const photos = rows.filter((row) => row.kind === 'photo').length;
  const what =
    photos === rows.length ? plural(photos, 'photo') : plural(rows.length, 'item');
  return `${what} could not be sent to the office and ${rows.length === 1 ? 'is' : 'are'} waiting here for you to decide. Everything is saved on this phone — nothing is lost.`;
}

function plural(count: number, one: string): string {
  return `${String(count)} ${count === 1 ? one : `${one}s`}`;
}
